import clsx from 'clsx'
import type { DayOfWeekPoint } from '@/db/queries'

interface Props {
  data: DayOfWeekPoint[]
  metricKey: 'recovery' | 'strain' | 'hrv' | 'sleep_minutes'
  format?: (value: number) => string
  /** Display range for the bar fill. If omitted, uses min/max from the data. */
  domain?: [number, number]
  color: string
  /** When true, lowest is highlighted instead of highest (e.g. "fastest pace" semantics). */
  lowerIsBetter?: boolean
}

/**
 * Apple-style horizontal day-of-week chart. Each day is one row: label,
 * filled bar, value, and a small delta from the overall mean.
 */
export default function DayOfWeekBars({
  data,
  metricKey,
  format = (v) => v.toFixed(1),
  domain,
  color,
  lowerIsBetter = false,
}: Props) {
  const values = data
    .map((d) => d[metricKey])
    .filter((v): v is number => typeof v === 'number')
  if (!values.length) {
    return <div className="text-sm text-stone-500 py-6">Not enough data yet.</div>
  }

  const dataMin = Math.min(...values)
  const dataMax = Math.max(...values)
  const mean = values.reduce((a, b) => a + b, 0) / values.length

  // Pad domain a touch so the bars don't touch the edges.
  const [lo, hi] = domain
    ? domain
    : [Math.max(0, dataMin - (dataMax - dataMin) * 0.15), dataMax + (dataMax - dataMin) * 0.05]
  const span = Math.max(1, hi - lo)

  // Identify the "best" day for a subtle accent.
  const bestVal = lowerIsBetter ? dataMin : dataMax

  return (
    <div>
      <div className="space-y-2.5">
        {data.map((d) => {
          const v = d[metricKey]
          if (typeof v !== 'number') {
            return (
              <Row key={d.dow} label={d.label} pct={0} color={color} value="—" muted />
            )
          }
          const pct = Math.max(2, ((v - lo) / span) * 100)
          const isBest = Math.abs(v - bestVal) < 0.01
          const delta = v - mean
          return (
            <Row
              key={d.dow}
              label={d.label}
              pct={pct}
              color={color}
              value={format(v)}
              best={isBest}
              delta={delta}
              format={format}
            />
          )
        })}
      </div>
      <div className="flex items-center justify-between mt-3 text-[11px] text-stone-500">
        <span>avg {format(mean)}</span>
        <span>n = {values.length} days</span>
      </div>
    </div>
  )
}

function Row({
  label,
  pct,
  color,
  value,
  best,
  delta,
  format,
  muted,
}: {
  label: string
  pct: number
  color: string
  value: string
  best?: boolean
  delta?: number
  format?: (v: number) => string
  muted?: boolean
}) {
  return (
    <div className="grid grid-cols-[40px_1fr_auto] items-center gap-3">
      <div className="text-xs text-stone-500 tabular-nums">{label}</div>
      <div className="relative h-6 rounded-md bg-stone-100 overflow-hidden">
        <div
          className={clsx(
            'absolute inset-y-0 left-0 rounded-md transition-all',
            muted && 'opacity-30',
          )}
          style={{
            width: `${pct}%`,
            background: best ? color : `${color}AA`,
            boxShadow: best ? `0 0 0 1px ${color}` : 'none',
          }}
        />
      </div>
      <div className="flex items-baseline gap-2 min-w-0">
        <div
          className={clsx(
            'text-sm tabular-nums',
            best ? 'text-stone-900 font-medium' : 'text-stone-700',
          )}
        >
          {value}
        </div>
        {delta != null && format && (
          <div className="text-[11px] text-stone-500 tabular-nums w-12 text-right">
            {delta >= 0 ? '+' : '−'}
            {format(Math.abs(delta))}
          </div>
        )}
      </div>
    </div>
  )
}
